import { useState } from 'react';
import type { ManagedOption } from '../../service/managed-options';

type Props = {
  isPathway: boolean;
  option?: ManagedOption;
  busy: boolean;
  onSubmit(change: Record<string, unknown>): void;
  onCancel(): void;
};

export default function OptionEditor({ option, isPathway, busy, onSubmit, onCancel }: Props) {
  const [action, setAction] = useState(option ? 'rename' : 'add');
  const [value, setValue] = useState(option?.value || '');
  const [crossGovernment, setCrossGovernment] = useState(
    option?.metadata.crossGovernmentDefault ?? true,
  );
  const [confirm, setConfirm] = useState(false);
  const actionLabels: Record<string, string> = {
    add: 'Create',
    retire: 'Delete',
    replace: 'Replace',
    rename: 'Rename',
    default: 'Change cross-government default',
  };
  const hints: Record<string, string> = {
    add: 'Add a new option',
    retire: 'Remove this option permanently with no replacement',
    replace:
      'Replace this option with updated text e.g. to use a new department name after it renames',
    rename: 'Rename the option text value e.g. to fix a typo',
  };
  const explanations: Record<string, string> = {
    rename:
      'Corrects a typo in this version. Updates all matching saved answers, including submitted forms. ID and version stay unchanged. You will review affected counts before confirming.',
    replace:
      'Retires this version and creates the next version with the same ID. Existing answers keep the old wording and version. New selections use the replacement.',
    retire:
      'Removes this version from new selections. Existing answers and their wording are preserved.',
    add: 'Adds a new value with a new ID and version 1.',
    default:
      'Changes the default for unanswered cross-government questions. Existing saved Yes/No answers, ID and version stay unchanged.',
  };
  return (
    <form
      className="govuk-!-padding-4 govuk-!-margin-bottom-6"
      style={{ border: '1px solid #b1b4b6' }}
      aria-labelledby="option-editor-heading"
      onSubmit={(event) => {
        event.preventDefault();
        if (action !== 'rename' && !confirm) {
          setConfirm(true);
          return;
        }
        onSubmit({
          action,
          value,
          ...(option && action !== 'add' ? { id: option.id, version: option.version } : {}),
          ...(isPathway ? { metadata: { crossGovernmentDefault: crossGovernment } } : {}),
        });
      }}
    >
      <h2 className="govuk-heading-m" id="option-editor-heading">
        {action === 'add'
          ? isPathway
            ? 'Create pathway'
            : 'Create department'
          : `Edit ${option?.value}`}
      </h2>
      {action !== 'default' && (
        <div className="govuk-form-group">
          <label className="govuk-label" htmlFor="option-action">
            Action
          </label>
          <select
            className="govuk-select"
            id="option-action"
            aria-describedby="option-action-hint"
            value={action}
            disabled={busy}
            onChange={(event) => {
              setAction(event.target.value);
              setValue(event.target.value === 'add' ? '' : option?.value || '');
              setCrossGovernment(
                event.target.value === 'add'
                  ? true
                  : (option?.metadata.crossGovernmentDefault ?? true),
              );
              setConfirm(false);
            }}
          >
            <option value="add">Create</option>
            <option value="retire" disabled={!option || Boolean(option.retired)}>
              Delete
            </option>
            <option value="replace" disabled={!option || Boolean(option.retired)}>
              Replace
            </option>
            <option value="rename" disabled={!option}>
              Rename
            </option>
          </select>
          <div className="govuk-hint govuk-!-margin-top-2" id="option-action-hint">
            {hints[action]}
          </div>
        </div>
      )}
      <p className="govuk-body">{explanations[action]}</p>
      {action !== 'retire' && action !== 'default' && (
        <div className="govuk-form-group">
          <label className="govuk-label" htmlFor="option-value">
            {action === 'rename'
              ? 'Corrected wording'
              : isPathway
                ? 'Pathway name'
                : 'Department name'}
          </label>
          <input
            className="govuk-input"
            id="option-value"
            required
            maxLength={200}
            value={value}
            onChange={(event) => {
              setValue(event.target.value);
              setConfirm(false);
            }}
          />
        </div>
      )}
      {isPathway && (action === 'add' || action === 'replace' || action === 'default') && (
        <div className="govuk-form-group">
          <label className="govuk-label" htmlFor="cross-government">
            Cross-government default
          </label>
          <select
            className="govuk-select"
            id="cross-government"
            value={String(crossGovernment)}
            onChange={(event) => {
              setCrossGovernment(event.target.value === 'true');
              setConfirm(false);
            }}
          >
            <option value="true">Yes</option>
            <option value="false">No</option>
          </select>
        </div>
      )}
      {confirm && (
        <p className="govuk-inset-text">
          Confirm {actionLabels[action]}: {action === 'add' ? '(new option)' : option?.value} →{' '}
          {action === 'retire'
            ? '(removed from new selections)'
            : action === 'default'
              ? `default ${crossGovernment ? 'Yes' : 'No'}`
              : value}
          . {explanations[action]}
        </p>
      )}
      <button className="govuk-button" type="submit" disabled={busy}>
        {action === 'rename'
          ? 'Preview affected answers'
          : confirm
            ? 'Confirm change'
            : 'Review change'}
      </button>{' '}
      {isPathway && option && !option.retired && action !== 'add' && (
        <>
          <button
            className="govuk-button govuk-button--secondary"
            type="button"
            disabled={busy}
            onClick={() => {
              setAction(action === 'default' ? 'rename' : 'default');
              setValue(option.value);
              setCrossGovernment(option.metadata.crossGovernmentDefault ?? true);
              setConfirm(false);
            }}
          >
            {action === 'default' ? 'Back to option actions' : 'Change cross-government default'}
          </button>{' '}
        </>
      )}
      <button
        className="govuk-button govuk-button--secondary"
        type="button"
        disabled={busy}
        onClick={onCancel}
      >
        Cancel
      </button>
    </form>
  );
}
